import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import apiCall from "./../../API/API";
import "../PlanSettings/PlanSettings.css";
import "./DepositAccounts.css";

const EMPTY = {
  bank: { id: "", bankName: "", holder: "", accountNumber: "", ifsc: "", ac_type: "Current", branch: "", status: 1, qrUrl: null },
  upi: { id: "", name: "", upiId: "", status: 1, qrUrl: null },
};

const QrThumb = ({ url, onOpen }) =>
  url ? (
    <button type="button" className="da-qr-thumb" onClick={() => onOpen(url)} title="View QR code">
      <img src={url} alt="QR code" />
    </button>
  ) : (
    <span className="ps-muted ps-small">No QR</span>
  );

const AccountModal = ({ show, type, initial, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY[type]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [removeQr, setRemoveQr] = useState(false);
  const [saving, setSaving] = useState(false);
  const isNew = !initial?.id;

  useEffect(() => {
    if (!show) return;
    setForm({ ...EMPTY[type], ...(initial || {}) });
    setFile(null);
    setPreview(null);
    setRemoveQr(false);
  }, [show, type, initial]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(f.type)) {
      toast.error("Choose a PNG, JPG, GIF or WEBP image.");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("The image must be smaller than 10 MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setRemoveQr(false);
  };

  const shownQr = preview || (!removeQr ? form.qrUrl : null);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("type", type);
    if (form.id) fd.append("id", form.id);
    fd.append("status", String(Number(form.status) === 1 ? 1 : 0));
    const keys = type === "bank" ? ["bankName", "holder", "accountNumber", "ifsc", "ac_type", "branch"] : ["name", "upiId"];
    keys.forEach((k) => fd.append(k, String(form[k] || "").trim()));
    if (file) fd.append("qr", file);
    if (removeQr && !file) fd.append("removeQr", "1");

    setSaving(true);
    const { data, error } = await apiCall("POST", "/deposit-accounts/save", fd);
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save.");
    toast.success(data?.message || "Saved.");
    onSaved();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isNew ? "Add" : "Edit"} {type === "bank" ? "Bank Account" : "UPI ID"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="da-modal-body">
            <div className="ps-form-grid da-fields">
              {type === "bank" ? (
                <>
                  <Form.Group>
                    <Form.Label>Bank name</Form.Label>
                    <Form.Control value={form.bankName} onChange={set("bankName")} required placeholder="e.g. HDFC Bank" />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Account holder</Form.Label>
                    <Form.Control value={form.holder} onChange={set("holder")} required placeholder="Name on the account" />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Account number</Form.Label>
                    <Form.Control value={form.accountNumber} onChange={set("accountNumber")} required inputMode="numeric" pattern="[0-9 ]{6,24}" />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>IFSC code</Form.Label>
                    <Form.Control
                      value={form.ifsc}
                      onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))}
                      required
                      maxLength={11}
                      placeholder="e.g. HDFC0001234"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Account type</Form.Label>
                    <Form.Select value={form.ac_type} onChange={set("ac_type")}>
                      <option value="Current">Current</option>
                      <option value="Savings">Savings</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Branch</Form.Label>
                    <Form.Control value={form.branch} onChange={set("branch")} placeholder="Optional" />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="ps-span-2">
                    <Form.Label>Display name</Form.Label>
                    <Form.Control value={form.name} onChange={set("name")} required placeholder="e.g. Polybionic Technologies" />
                  </Form.Group>
                  <Form.Group className="ps-span-2">
                    <Form.Label>UPI ID</Form.Label>
                    <Form.Control value={form.upiId} onChange={set("upiId")} required placeholder="e.g. polybionic@okhdfcbank" />
                  </Form.Group>
                </>
              )}
              <Form.Group className="ps-span-2">
                <Form.Check
                  type="switch"
                  id="deposit-status"
                  label={Number(form.status) === 1 ? "Shown to users when they pay for a package" : "Hidden from users"}
                  checked={Number(form.status) === 1}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
                />
              </Form.Group>
            </div>

            <div className="da-qr-box">
              <Form.Label>QR code</Form.Label>
              <div className="da-qr-preview">
                {shownQr ? <img src={shownQr} alt="QR code preview" /> : <span>No QR code</span>}
              </div>
              <Form.Control type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={pickFile} size="sm" />
              <Form.Text>PNG, JPG, GIF or WEBP, up to 10 MB.</Form.Text>
              {form.qrUrl && !file && (
                <Form.Check
                  className="mt-2"
                  type="checkbox"
                  id="remove-qr"
                  label="Remove current QR"
                  checked={removeQr}
                  onChange={(e) => setRemoveQr(e.target.checked)}
                />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" /> : isNew ? "Add" : "Save Changes"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AccountTable = ({ type, rows, onEdit, onToggle, onDelete, busyId, onOpenQr }) => (
  <div className="ps-table-wrap">
    <Table hover className="ps-table">
      <thead>
        <tr>
          <th>QR</th>
          {type === "bank" ? (
            <>
              <th>Bank</th>
              <th>Account</th>
              <th>IFSC</th>
            </>
          ) : (
            <>
              <th>Name</th>
              <th>UPI ID</th>
            </>
          )}
          <th>Status</th>
          <th className="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={type === "bank" ? 6 : 5} className="ps-empty">
              No {type === "bank" ? "bank accounts" : "UPI IDs"} yet. Users will not see this method until you add one.
            </td>
          </tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className={r.status === 1 ? "" : "ps-row-muted"}>
            <td><QrThumb url={r.qrUrl} onOpen={onOpenQr} /></td>
            {type === "bank" ? (
              <>
                <td>
                  <div className="ps-strong">{r.bankName}</div>
                  <div className="ps-muted ps-small">{r.holder}{r.branch ? ` · ${r.branch}` : ""}</div>
                </td>
                <td>
                  <div>{r.accountNumber}</div>
                  <div className="ps-muted ps-small">{r.ac_type}</div>
                </td>
                <td>{r.ifsc}</td>
              </>
            ) : (
              <>
                <td className="ps-strong">{r.name}</td>
                <td>{r.upiId}</td>
              </>
            )}
            <td>
              <Badge bg={r.status === 1 ? "success" : "secondary"} className="ps-badge">
                {r.status === 1 ? "Shown" : "Hidden"}
              </Badge>
            </td>
            <td className="text-end ps-actions">
              <Button size="sm" variant="outline-primary" onClick={() => onEdit(r)}>Edit</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => onToggle(r)} disabled={busyId === r.id}>
                {busyId === r.id ? <Spinner size="sm" /> : r.status === 1 ? "Hide" : "Show"}
              </Button>
              <Button size="sm" variant="outline-danger" onClick={() => onDelete(r)} disabled={busyId === r.id}>Delete</Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

const DepositAccounts = () => {
  const [data, setData] = useState({ bank: [], upi: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bank");
  const [modal, setModal] = useState({ show: false, type: "bank", row: null });
  const [busyId, setBusyId] = useState(null);
  const [qrView, setQrView] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    const { data: res, error } = await apiCall("GET", "/deposit-accounts");
    if (error) toast.error(error.message || "Could not load deposit accounts.");
    else setData(res?.data || { bank: [], upi: [] });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (type, r) => {
    setBusyId(r.id);
    const fd = new FormData();
    fd.append("type", type);
    fd.append("id", r.id);
    fd.append("status", r.status === 1 ? "0" : "1");
    const keys = type === "bank" ? ["bankName", "holder", "accountNumber", "ifsc", "ac_type", "branch"] : ["name", "upiId"];
    keys.forEach((k) => fd.append(k, r[k] || ""));
    const { error } = await apiCall("POST", "/deposit-accounts/save", fd);
    setBusyId(null);
    if (error) return toast.error(error.message || "Could not change visibility.");
    toast.success(r.status === 1 ? "Hidden from users." : "Shown to users.");
    load();
  };

  const remove = async () => {
    const { type, row } = confirmDelete;
    setBusyId(row.id);
    const { data: res, error } = await apiCall("POST", "/deposit-accounts/delete", { type, id: row.id });
    setBusyId(null);
    setConfirmDelete(null);
    if (error) return toast.error(error.message || "Could not delete.");
    toast.success(res?.message || "Removed.");
    load();
  };

  const shown = (list) => list.filter((r) => r.status === 1).length;

  return (
    <div className="plan-settings deposit-accounts">
      <div className="ps-header">
        <div>
          <h2>Deposit Accounts</h2>
          <p>Bank accounts and UPI IDs members pay into when they request a package activation. Hidden accounts are kept but not shown to users.</p>
        </div>
      </div>

      {loading ? (
        <div className="ps-loading"><Spinner /> Loading…</div>
      ) : (
        <>
          <div className="ps-stats da-stats">
            <div className="ps-stat">
              <span>Bank accounts shown</span>
              <strong>{shown(data.bank)} <small>/ {data.bank.length}</small></strong>
            </div>
            <div className="ps-stat">
              <span>UPI IDs shown</span>
              <strong>{shown(data.upi)} <small>/ {data.upi.length}</small></strong>
            </div>
          </div>

          <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="ps-tabs">
            {["bank", "upi"].map((type) => (
              <Tab key={type} eventKey={type} title={type === "bank" ? "Bank Accounts" : "UPI IDs"}>
                <div className="ps-panel">
                  <div className="ps-panel-head">
                    <div>
                      <h3>{type === "bank" ? "Bank Accounts" : "UPI IDs"}</h3>
                      <p>
                        {type === "bank"
                          ? "For NEFT / IMPS / RTGS transfers. Add a QR code if the account has one."
                          : "Users scan the QR code or pay to the UPI ID from any UPI app."}
                      </p>
                    </div>
                    <Button onClick={() => setModal({ show: true, type, row: null })}>
                      + Add {type === "bank" ? "Bank Account" : "UPI ID"}
                    </Button>
                  </div>
                  <AccountTable
                    type={type}
                    rows={data[type]}
                    busyId={busyId}
                    onOpenQr={setQrView}
                    onEdit={(row) => setModal({ show: true, type, row })}
                    onToggle={(row) => toggle(type, row)}
                    onDelete={(row) => setConfirmDelete({ type, row })}
                  />
                </div>
              </Tab>
            ))}
          </Tabs>
        </>
      )}

      <AccountModal
        show={modal.show}
        type={modal.type}
        initial={modal.row}
        onClose={() => setModal((m) => ({ ...m, show: false }))}
        onSaved={() => { setModal((m) => ({ ...m, show: false })); load(); }}
      />

      <Modal show={!!qrView} onHide={() => setQrView(null)} centered size="sm" className="plan-settings-modal">
        <Modal.Body className="text-center">
          {qrView && <img src={qrView} alt="QR code" className="da-qr-full" />}
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)} centered className="plan-settings-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete account?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmDelete && (
            <p className="mb-0">
              {confirmDelete.type === "bank"
                ? `${confirmDelete.row.bankName} · ${confirmDelete.row.accountNumber}`
                : confirmDelete.row.upiId}{" "}
              and its QR code will be removed. Past deposit requests keep their record. To stop showing it for now,
              use Hide instead.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="danger" onClick={remove} disabled={!!busyId}>{busyId ? <Spinner size="sm" /> : "Delete"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DepositAccounts;
